import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import RefreshIcon from '@mui/icons-material/Refresh'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format } from 'date-fns'
import { reconciliationService } from '@/services/reconciliationService'

interface ReconciliationRecord {
  date: string
  deposits: {
    count: number
    amount: number
    reconciled: number
    variance: number
  }
  withdrawals: {
    count: number
    amount: number
    reconciled: number
    variance: number
  }
  status: 'reconciled' | 'pending' | 'variance'
  variance: number
}

const Reconciliation = () => {
  const [records, setRecords] = useState<ReconciliationRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [date, setDate] = useState<Date | null>(new Date())
  const [reconciliationStatus, setReconciliationStatus] = useState<'all' | 'reconciled' | 'pending' | 'variance'>('all')

  useEffect(() => {
    if (date) {
      loadReconciliation()
    }
  }, [date])

  const loadReconciliation = async () => {
    if (!date) return
    setLoading(true)
    try {
      const data = await reconciliationService.getReconciliation(format(date, 'yyyy-MM-dd'))
      setRecords(data)
    } catch (error) {
      console.error('Failed to load reconciliation', error)
      setRecords([])
    } finally {
      setLoading(false)
    }
  }

  const filteredRecords = useMemo(() => {
    if (reconciliationStatus === 'all') return records
    return records.filter((r) => r.status === reconciliationStatus)
  }, [records, reconciliationStatus])

  const summary = useMemo(() => {
    return {
      totalDeposits: records.reduce((sum, r) => sum + r.deposits.amount, 0),
      totalWithdrawals: records.reduce((sum, r) => sum + r.withdrawals.amount, 0),
      totalVariance: records.reduce((sum, r) => sum + r.variance, 0),
      reconciled: records.filter((r) => r.status === 'reconciled').length,
      pending: records.filter((r) => r.status === 'pending').length,
      variance: records.filter((r) => r.status === 'variance').length,
    }
  }, [records])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      maximumFractionDigits: 0,
    }).format(value)
  }

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'reconciled':
        return 'success'
      case 'variance':
        return 'error'
      default:
        return 'warning'
    }
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Financial Reconciliation
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Daily reconciliation reports and variance tracking
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Total Deposits
              </Typography>
              <Typography variant="h5">{formatCurrency(summary.totalDeposits)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Total Withdrawals
              </Typography>
              <Typography variant="h5">{formatCurrency(summary.totalWithdrawals)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Total Variance
              </Typography>
              <Typography variant="h5" color={summary.totalVariance === 0 ? 'success.main' : 'error.main'}>
                {formatCurrency(summary.totalVariance)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Reconciled Days
              </Typography>
              <Typography variant="h5" color="success.main">
                {summary.reconciled} / {records.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Date"
                value={date}
                onChange={setDate}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={reconciliationStatus}
                  label="Status"
                  onChange={(e) => setReconciliationStatus(e.target.value as any)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="reconciled">Reconciled</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="variance">Variance</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={loadReconciliation}
                fullWidth
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </LocalizationProvider>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Reconciliation Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="right">Deposits Count</TableCell>
              <TableCell align="right">Deposits Amount</TableCell>
              <TableCell align="right">Deposits Reconciled</TableCell>
              <TableCell align="right">Deposits Variance</TableCell>
              <TableCell align="right">Withdrawals Count</TableCell>
              <TableCell align="right">Withdrawals Amount</TableCell>
              <TableCell align="right">Withdrawals Reconciled</TableCell>
              <TableCell align="right">Withdrawals Variance</TableCell>
              <TableCell align="right">Total Variance</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRecords.map((record) => (
              <TableRow key={record.date} hover>
                <TableCell>
                  <Typography variant="body2">{format(new Date(record.date), 'MMM dd, yyyy')}</Typography>
                </TableCell>
                <TableCell align="right">{record.deposits.count}</TableCell>
                <TableCell align="right">{formatCurrency(record.deposits.amount)}</TableCell>
                <TableCell align="right">{record.deposits.reconciled}%</TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    color={record.deposits.variance === 0 ? 'text.primary' : 'error.main'}
                  >
                    {formatCurrency(record.deposits.variance)}
                  </Typography>
                </TableCell>
                <TableCell align="right">{record.withdrawals.count}</TableCell>
                <TableCell align="right">{formatCurrency(record.withdrawals.amount)}</TableCell>
                <TableCell align="right">{record.withdrawals.reconciled}%</TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    color={record.withdrawals.variance === 0 ? 'text.primary' : 'error.main'}
                  >
                    {formatCurrency(record.withdrawals.variance)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color={record.variance === 0 ? 'success.main' : 'error.main'}
                  >
                    {formatCurrency(record.variance)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={record.status.toUpperCase()}
                    size="small"
                    color={getStatusColor(record.status)}
                    icon={
                      record.status === 'reconciled' ? (
                        <CheckCircleIcon />
                      ) : record.status === 'variance' ? (
                        <ErrorIcon />
                      ) : (
                        <WarningIcon />
                      )
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default Reconciliation

